import { KeyboardEvent, useEffect, useRef, useState } from "react";

import { fetchBuilding, fetchPlace } from "@/apis/place";
import {
  createBuilding,
  createPlace,
  deleteBuilding,
  deletePlace,
  updateBuilding,
  updatePlace,
} from "@/apis/admin";
import Button from "@/components/Button";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import useModal from "@/hooks/useModal";
import PlaceDetailModal from "@/pages/Admin/components/PlaceManagement/PlaceDetailModal";

interface Building {
  bldCd: string;
  bldNm: string;
  cpsCd: string;
}

interface PlaceItem {
  plcCd: string;
  plcNm: string;
}

interface PlacesByFloor {
  floor: string;
  places: PlaceItem[];
}

const CAMPUS_CD = "PTK";

const PlaceManagement = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBldCd, setSelectedBldCd] = useState<string | null>(null);
  const [placesByFloor, setPlacesByFloor] = useState<PlacesByFloor[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<string | null>(null);

  // 건물 추가 인라인 폼
  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [newBldNm, setNewBldNm] = useState("");

  // 건물 인라인 편집
  const [editBldCd, setEditBldCd] = useState<string | null>(null);
  const [editBldNm, setEditBldNm] = useState("");

  // 장소 추가 인라인 폼
  const [showAddPlace, setShowAddPlace] = useState(false);
  const [newPlcNm, setNewPlcNm] = useState("");
  const [newPlcF, setNewPlcF] = useState("");

  // 장소 인라인 편집
  const [editPlcCd, setEditPlcCd] = useState<string | null>(null);
  const [editPlcNm, setEditPlcNm] = useState("");
  const [editPlcF, setEditPlcF] = useState("");
  // 삭제 모드
  const [deleteMode, setDeleteMode] = useState(false);
  const [detailPlcCd, setDetailPlcCd] = useState<string | null>(null);
  const [detailPlcNm, setDetailPlcNm] = useState("");
  const [detailPlcF, setDetailPlcF] = useState(0);

  // 액션 로딩 상태 (중복 클릭 방지)
  const [actionLoading, setActionLoading] = useState(false);

  // 삭제 확인 모달
  const confirmModal = useModal();
  const [pendingDelete, setPendingDelete] = useState<{
    type: "building" | "place";
    cd: string;
    nm: string;
  } | null>(null);

  const addBldNmRef = useRef<HTMLInputElement>(null);
  const addPlcNmRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBldCd) {
      loadPlaces(selectedBldCd);
    } else {
      setPlacesByFloor([]);
      setSelectedFloor(null);
    }
  }, [selectedBldCd]);

  const loadBuildings = async () => {
    try {
      const res = await fetchBuilding();
      const list: Building[] = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      setBuildings(list);
    } catch (e) {
      console.error(e);
    }
  };

  const loadPlaces = async (bldCd: string) => {
    try {
      const res = await fetchPlace(bldCd);
      const list: PlacesByFloor[] = Array.isArray(res) ? res : [];
      setPlacesByFloor(list);
      setSelectedFloor(list.length > 0 ? list[0].floor : null);
    } catch (e) {
      console.error(e);
    }
  };

  // ---- 건물 CRUD ----

  const handleAddBuilding = async () => {
    if (!newBldNm.trim() || actionLoading) return;
    setActionLoading(true);
    try {
      await createBuilding({ cpsCd: CAMPUS_CD, bldNm: newBldNm.trim() });
      setNewBldNm("");
      setShowAddBuilding(false);
      await loadBuildings();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartEditBuilding = (bld: Building) => {
    setEditBldCd(bld.bldCd);
    setEditBldNm(bld.bldNm);
  };

  const handleSaveBuilding = async (bldCd: string) => {
    if (!editBldNm.trim() || actionLoading) return;
    setActionLoading(true);
    try {
      await updateBuilding({ bldCd, bldNm: editBldNm.trim() });
      setEditBldCd(null);
      await loadBuildings();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDeleteBuilding = (bld: Building) => {
    setPendingDelete({ type: "building", cd: bld.bldCd, nm: bld.bldNm });
    confirmModal.onOpen();
  };

  // ---- 장소 CRUD ----

  const handleAddPlace = async () => {
    if (!newPlcNm.trim() || !newPlcF.trim() || !selectedBldCd || actionLoading) return;
    const floor = parseInt(newPlcF, 10);
    if (isNaN(floor)) return;
    setActionLoading(true);
    try {
      await createPlace({
        cpsCd: CAMPUS_CD,
        bldCd: selectedBldCd,
        plcNm: newPlcNm.trim(),
        plcF: floor,
      });
      setNewPlcNm("");
      setNewPlcF("");
      setShowAddPlace(false);
      await loadPlaces(selectedBldCd);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSavePlace = async (plcCd: string) => {
    if (!editPlcNm.trim() || !editPlcF.trim() || actionLoading) return;
    const floorNum = parseInt(editPlcF, 10);
    if (isNaN(floorNum)) return;
    setActionLoading(true);
    try {
      await updatePlace({ plcCd, plcNm: editPlcNm.trim(), plcF: floorNum });
      setEditPlcCd(null);
      if (selectedBldCd) await loadPlaces(selectedBldCd);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDeletePlace = (plc: PlaceItem) => {
    setPendingDelete({ type: "place", cd: plc.plcCd, nm: plc.plcNm });
    confirmModal.onOpen();
  };

  // ---- 삭제 확인 ----

  const handleConfirmDelete = async () => {
    if (!pendingDelete || actionLoading) return;
    setActionLoading(true);
    try {
      if (pendingDelete.type === "building") {
        await deleteBuilding(pendingDelete.cd);
        if (selectedBldCd === pendingDelete.cd) {
          setSelectedBldCd(null);
        }
        await loadBuildings();
      } else {
        await deletePlace(pendingDelete.cd);
        if (selectedBldCd) await loadPlaces(selectedBldCd);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
      confirmModal.onClose();
      setPendingDelete(null);
    }
  };

  const selectedFloorPlaces =
    placesByFloor.find(pf => pf.floor === selectedFloor)?.places ?? [];

  const handleBldKeyDown = (e: KeyboardEvent<HTMLInputElement>, bldCd: string) => {
    if (e.key === "Enter") handleSaveBuilding(bldCd);
    if (e.key === "Escape") setEditBldCd(null);
  };

  const handlePlcKeyDown = (e: KeyboardEvent<HTMLInputElement>, plcCd: string) => {
    if (e.key === "Enter") handleSavePlace(plcCd);
    if (e.key === "Escape") setEditPlcCd(null);
  };

  return (
    <div className="flex w-full flex-col gap-4 p-4 text-base text-black">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-small leading-relaxed text-amber-800 flex-1">
          <p className="font-bold">* 주의사항</p>
          <p className="mt-1">장소 및 건물을 임의로 삭제하거나 수정할 경우, 기존 예약 데이터가 손상될 수 있습니다.</p>
          <p>전반적인 수정이 필요하시다면 시스템 관리자에게 문의해주세요.</p>
          <p className="mt-1">삭제 기능은 우측의 '삭제 모드' 버튼을 활성화한 후 사용할 수 있습니다.</p>
        </div>
        <button
          type="button"
          onClick={() => setDeleteMode(v => !v)}
          className={`shrink-0 rounded-lg border px-4 py-2 text-xs font-medium transition-all ${
            deleteMode
              ? "border-red-300 bg-red-50 text-red-600"
              : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
          }`}
        >
          {deleteMode ? "삭제 모드 ON" : "삭제 모드 OFF"}
        </button>
      </div>
      <div className="flex gap-4">
        {/* 왼쪽: 건물 목록 */}
        <div className="flex w-64 shrink-0 flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-dark">건물 목록</span>
            <Button
              variant="add"
              className="text-small"
              onClick={() => {
                setShowAddBuilding(v => !v);
                setTimeout(() => addBldNmRef.current?.focus(), 50);
              }}
            >
              건물 추가
            </Button>
          </div>

          {showAddBuilding && (
            <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-2">
              <input
                ref={addBldNmRef}
                className="rounded-lg border border-gray-300 px-2 py-1 text-small outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                placeholder="건물명"
                value={newBldNm}
                onChange={e => setNewBldNm(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleAddBuilding();
                  if (e.key === "Escape") setShowAddBuilding(false);
                }}
              />
              <div className="flex gap-1">
                <Button variant="primary" className="text-small flex-1" onClick={handleAddBuilding}>
                  추가
                </Button>
                <Button
                  variant="outlined"
                  className="text-small flex-1"
                  onClick={() => setShowAddBuilding(false)}
                >
                  취소
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col rounded-lg border border-gray-200 bg-white">
            {buildings.length === 0 ? (
              <p className="p-3 text-small text-gray-dull">건물 없음</p>
            ) : (
              buildings.map(bld => (
                <div key={bld.bldCd}>
                  {/* 건물 행 */}
                  <div
                    className={`group flex cursor-pointer items-center justify-between gap-1 px-3 py-2 transition-all duration-150 ${
                      selectedBldCd === bld.bldCd
                        ? "bg-primary text-white"
                        : "hover:-translate-y-px hover:bg-blue-50/40 hover:shadow-md"
                    }`}
                    onClick={() => {
                      if (editBldCd !== bld.bldCd) {
                        setSelectedBldCd(bld.bldCd);
                      }
                    }}
                  >
                    {editBldCd === bld.bldCd ? (
                      <input
                        className="flex-1 rounded-sm border border-gray-dull px-2 py-0.5 text-small text-black outline-none focus:border-primary"
                        value={editBldNm}
                        onChange={e => setEditBldNm(e.target.value)}
                        onKeyDown={e => handleBldKeyDown(e, bld.bldCd)}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      <span className="flex-1 truncate text-small font-medium">
                        {bld.bldNm}
                      </span>
                    )}
                    <div
                      className={`flex shrink-0 gap-1 ${
                        editBldCd === bld.bldCd
                          ? "visible"
                          : "invisible group-hover:visible"
                      }`}
                      onClick={e => e.stopPropagation()}
                    >
                      {editBldCd === bld.bldCd ? (
                        <>
                          <button
                            className="text-small text-green-600 hover:underline"
                            onClick={() => handleSaveBuilding(bld.bldCd)}
                          >
                            저장
                          </button>
                          <button
                            className="text-small text-gray-dull hover:underline"
                            onClick={() => setEditBldCd(null)}
                          >
                            취소
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className={`text-small hover:underline ${
                              selectedBldCd === bld.bldCd
                                ? "text-white"
                                : "text-primary"
                            }`}
                            onClick={() => handleStartEditBuilding(bld)}
                          >
                            편집
                          </button>
                          {deleteMode && (
                            <button
                              className={`text-small hover:underline ${
                                selectedBldCd === bld.bldCd
                                  ? "text-white"
                                  : "text-red-500"
                              }`}
                              onClick={() => handleRequestDeleteBuilding(bld)}
                            >
                              삭제
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* 층 목록 */}
                  {selectedBldCd === bld.bldCd && placesByFloor.length > 0 && (
                    <div className="flex flex-col bg-white-dull">
                      {placesByFloor.map(pf => (
                        <button
                          key={pf.floor}
                          className={`px-6 py-1.5 text-left text-small ${
                            selectedFloor === pf.floor
                              ? "bg-primary/10 font-medium text-primary"
                              : "text-gray-dark hover:bg-gray-50"
                          }`}
                          onClick={() => setSelectedFloor(pf.floor)}
                        >
                          {pf.floor}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* 오른쪽: 장소 목록 */}
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-dark">
              {selectedBldCd && selectedFloor
                ? `${buildings.find(b => b.bldCd === selectedBldCd)?.bldNm ?? ""} · ${selectedFloor} 장소 목록`
                : "장소 목록"}
            </span>
            {selectedBldCd && (
              <Button
                variant="add"
                className="text-small"
                onClick={() => {
                  setShowAddPlace(v => !v);
                  setTimeout(() => addPlcNmRef.current?.focus(), 50);
                }}
              >
                장소 추가
              </Button>
            )}
          </div>

          {showAddPlace && selectedBldCd && (
            <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-white p-2">
              <input
                ref={addPlcNmRef}
                className="w-40 rounded-lg border border-gray-300 px-2 py-1 text-small outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                placeholder="장소명"
                value={newPlcNm}
                onChange={e => setNewPlcNm(e.target.value)}
              />
              <input
                className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-small outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                placeholder="층수 (숫자)"
                type="number"
                value={newPlcF}
                onChange={e => setNewPlcF(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleAddPlace();
                  if (e.key === "Escape") setShowAddPlace(false);
                }}
              />
              <Button variant="primary" className="text-small" onClick={handleAddPlace}>
                추가
              </Button>
              <Button
                variant="outlined"
                className="text-small"
                onClick={() => setShowAddPlace(false)}
              >
                취소
              </Button>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            {!selectedBldCd ? (
              <p className="p-4 text-small text-gray-dull">
                왼쪽에서 건물을 선택하세요.
              </p>
            ) : selectedFloorPlaces.length === 0 ? (
              <p className="p-4 text-small text-gray-dull">
                장소가 없습니다. 장소를 추가해 주세요.
              </p>
            ) : (
              <table className="w-full text-small">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="border-b border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">장소코드</th>
                    <th className="border-b border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">장소명</th>
                    <th className="border-b border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">층</th>
                    <th className="border-b border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedFloorPlaces.map(plc => {
                    const isEditing = editPlcCd === plc.plcCd;
                    return (
                      <tr
                        key={plc.plcCd}
                        className="transition-all duration-150 hover:-translate-y-px hover:bg-blue-50/40 hover:shadow-md"
                      >
                        <td className="px-4 py-3 text-sm text-gray-500">{plc.plcCd}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {isEditing ? (
                            <input
                              className="w-full rounded-lg border border-gray-300 px-2 py-0.5 text-small outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                              value={editPlcNm}
                              onChange={e => setEditPlcNm(e.target.value)}
                              onKeyDown={e => handlePlcKeyDown(e, plc.plcCd)}
                              autoFocus
                            />
                          ) : (
                            plc.plcNm
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {isEditing ? (
                            <input
                              type="number"
                              className="w-16 rounded-lg border border-gray-300 px-2 py-0.5 text-small outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                              value={editPlcF}
                              onChange={e => setEditPlcF(e.target.value)}
                              onKeyDown={e => handlePlcKeyDown(e, plc.plcCd)}
                            />
                          ) : (
                            selectedFloor
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {isEditing ? (
                              <>
                                <button
                                  className="text-small text-green-600 hover:underline"
                                  onClick={() => handleSavePlace(plc.plcCd)}
                                >
                                  저장
                                </button>
                                <button
                                  className="text-small text-gray-dull hover:underline"
                                  onClick={() => setEditPlcCd(null)}
                                >
                                  취소
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="text-small text-primary hover:underline"
                                  onClick={() => {
                                    setDetailPlcCd(plc.plcCd);
                                    setDetailPlcNm(plc.plcNm);
                                    setDetailPlcF(selectedFloor ? parseInt(selectedFloor.replace(/[^0-9]/g, ""), 10) || 0 : 0);
                                  }}
                                >
                                  상세
                                </button>
                                {deleteMode && (
                                  <button
                                    className="text-small text-red-500 hover:underline"
                                    onClick={() => handleRequestDeletePlace(plc)}
                                  >
                                    삭제
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {confirmModal.isOpen && pendingDelete && (
        <ConfirmModal
          title="삭제 확인"
          onConfirm={handleConfirmDelete}
          onClose={() => {
            confirmModal.onClose();
            setPendingDelete(null);
          }}
        >
          {pendingDelete.type === "building"
            ? `'${pendingDelete.nm}' 건물을 삭제하시겠습니까?`
            : `'${pendingDelete.nm}' 장소를 삭제하시겠습니까?`}
        </ConfirmModal>
      )}
      {detailPlcCd && (
        <PlaceDetailModal
          plcCd={detailPlcCd}
          initialPlcNm={detailPlcNm}
          initialPlcF={detailPlcF}
          onClose={() => setDetailPlcCd(null)}
          onSaved={() => {
            if (selectedBldCd) loadPlaces(selectedBldCd);
          }}
        />
      )}
    </div>
  );
};

export default PlaceManagement;
